import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { setPrivacy, isPrivacy } from "@/lib/privacy";
import { toast } from "sonner";

export default function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [hideAmounts, setHideAmounts] = useState(isPrivacy());

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      setPrivacy(hideAmounts);   // deciso prima del login: nessun flash di importi reali
      await login(email.replace(/[\s\u00a0\u200b-\u200d\ufeff]/g, ""), password.trim());      toast.success("Benvenuto in PuraVida Control");
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2E5C60 0%, #1F4245 100%)" }}>
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <img src="/brand/puravida-logo.png" alt="PuraVida Home" className="rounded-2xl shadow-2xl" style={{ maxWidth: 230 }} />
            <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[color:var(--pv-mint-soft)] font-semibold">PuraVida Control</div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-5xl font-extrabold leading-tight">Gestisci i tuoi<br/>affitti brevi<br/><span className="text-[color:var(--pv-mint)]">con serenità.</span></h1>
            <p className="mt-6 text-[color:var(--pv-mint-soft)] leading-relaxed">Rendiconti proprietari, pulizie, lavanderia, manutenzioni. Tutto in un solo posto, in italiano, veloce da iPhone e desktop.</p>
          </div>
          <div className="text-xs text-[color:var(--pv-mint)] uppercase tracking-widest">Gestione professionale · PuraVida Home</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-[var(--bg-main)]">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="md:hidden mb-4 flex flex-col items-center text-center">
            <img src="/brand/puravida-logo.png" alt="PuraVida Home" className="rounded-xl" style={{ maxWidth: 190 }} />
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">PuraVida Control</div>
          </div>
          <div>
            <h2 className="font-display text-3xl font-bold">Accedi</h2>
            <p className="text-sm text-slate-500 mt-1">Inserisci le tue credenziali</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input id="username" data-testid="login-email-input" type="text" required autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="puravida" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
          <Button type="submit" disabled={submitting} data-testid="login-submit-btn" className="w-full bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] text-white font-semibold py-6 rounded-xl">{submitting ? "Accesso..." : "Entra"}</Button>

          <label className="flex items-start gap-3 pt-1 cursor-pointer select-none" data-testid="login-privacy-label">
            <input type="checkbox" checked={hideAmounts} onChange={(e) => setHideAmounts(e.target.checked)}
              data-testid="login-privacy-check"
              className="mt-0.5 h-5 w-5 shrink-0 rounded-md border-2 border-slate-300 accent-[var(--teal-deep)] cursor-pointer"
              style={{ accentColor: "var(--teal-deep)" }} />
            <span>
              <span className="block text-sm font-medium text-slate-700">Nascondi importi all'accesso</span>
              <span className="block text-[11px] text-slate-400 mt-0.5">La Dashboard si aprirà con i valori economici nascosti.</span>
            </span>
          </label>
          <p className="text-xs text-slate-400 text-center">Applicazione privata · accesso su invito</p>
        </form>
      </div>
    </div>
  );
}
